"use client"

import { useState, useEffect } from "react"
import { useAuth } from "../../../contexts/AuthContext"
import { useNavigate } from "react-router-dom"
import Swal from "sweetalert2"
import { MagnifyingGlassIcon, UserIcon, PencilIcon, CheckIcon, XMarkIcon, BanknotesIcon, TrashIcon } from "@heroicons/react/24/outline"

export default function MetaPricingPage() {
  const { user, getAllUsersPricing, setUserPricing, setUserBilling, getUserCountryPricing, setUserCountryPricing, addMasterCountry, deleteMasterCountry, fetchCampaignHistory } = useAuth()
  const navigate = useNavigate()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [page, setPage] = useState(1)
  const [limit] = useState(10)
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1 })
  const [selectedUser, setSelectedUser] = useState(null)
  const [history, setHistory] = useState([])
  const [historySummary, setHistorySummary] = useState({})
  const [historyPagination, setHistoryPagination] = useState({ currentPage: 1, totalPages: 1, totalRecords: 0 })
  const [historyPage, setHistoryPage] = useState(1)
  const [historyLimit] = useState(10)
  const [editingPricing, setEditingPricing] = useState({})
  const [defaultPricing, setDefaultPricing] = useState({})
  const [showCountryRates, setShowCountryRates] = useState(false)
  const [countryFilter, setCountryFilter] = useState("")

  // Country Pricing Modal States
  const [countryModalUser, setCountryModalUser] = useState(null)
  const [countryModalLoading, setCountryModalLoading] = useState(false)
  const [countryModalCountries, setCountryModalCountries] = useState([])
  const [countryModalAllowInt, setCountryModalAllowInt] = useState(false)
  const [countryModalSearch, setCountryModalSearch] = useState("")

  // Add Custom Country Form State
  const [showAddCountryForm, setShowAddCountryForm] = useState(false)
  const [newCountryName, setNewCountryName] = useState("")
  const [newCountryCode, setNewCountryCode] = useState("")
  const [newMarketingDisplay, setNewMarketingDisplay] = useState("")
  const [newMarketingDeduct, setNewMarketingDeduct] = useState("")
  const [newUtilityDisplay, setNewUtilityDisplay] = useState("")
  const [newUtilityDeduct, setNewUtilityDeduct] = useState("")
  const [newAuthDisplay, setNewAuthDisplay] = useState("")
  const [newAuthDeduct, setNewAuthDeduct] = useState("")

  useEffect(() => {
    // fetch on mount and whenever page or search changes
    const t = setTimeout(() => fetchAllUsersPricing(), 300) // debounce search
    return () => clearTimeout(t)
  }, [page, searchTerm])

  useEffect(() => {
    if (!["superadmin", "admin"].includes(user?.role)) return
    loadHistory()
  }, [selectedUser, historyPage, user?.role])

  const fetchAllUsersPricing = async () => {
    try {
      setLoading(true)
      const result = await getAllUsersPricing(page, limit, searchTerm)

      if (result.success) {
        setUsers(result.users || [])
        setDefaultPricing(result.defaultPricing || {})
        setPagination(result.pagination || { currentPage: page, totalPages: 1 })
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: result.error || "Failed to fetch users pricing",
          confirmButtonColor: "#4A7C59",
        })
      }
    } catch (error) {
      console.error("Error fetching users pricing:", error)
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch users pricing",
        confirmButtonColor: "#4A7C59",
      })
    } finally {
      setLoading(false)
    }
  }

  const loadHistory = async () => {
    if (!["superadmin", "admin"].includes(user?.role)) return

    try {
      setHistoryLoading(true)
      const result = await fetchCampaignHistory(selectedUser?.userId || null, {}, historyPage, historyLimit)
      if (result.success) {
        setHistory(result.history || [])
        setHistorySummary(result.summary || {})
        setHistoryPagination(result.pagination || { currentPage: historyPage, totalPages: 1, totalRecords: 0 })
      }
    } catch (error) {
      console.error("Error loading meta history:", error)
    } finally {
      setHistoryLoading(false)
    }
  }

  const selectHistoryUser = (targetUser = null) => {
    setSelectedUser(targetUser)
    setHistoryPage(1)
  }

  const handleSetPricing = async (userId, messageType, price) => {
    try {
      if (!price || isNaN(Number.parseFloat(price))) {
        Swal.fire({
          icon: "error",
          title: "Invalid Price",
          text: "Please enter a valid price",
          confirmButtonColor: "#4A7C59",
        })
        return
      }

      const result = await setUserPricing(userId, {
        [messageType]: Number.parseFloat(price),
      })

      if (result.success) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `${messageType} pricing updated successfully`,
          confirmButtonColor: "#4A7C59",
        })
        fetchAllUsersPricing()
        setEditingPricing({})
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: result.error || "Failed to update pricing",
          confirmButtonColor: "#4A7C59",
        })
      }
    } catch (error) {
      console.error("Error setting pricing:", error)
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update pricing",
        confirmButtonColor: "#4A7C59",
      })
    }
  }

  const startEditing = (userId, messageType, currentPrice) => {
    setEditingPricing({
      [`${userId}-${messageType}`]: currentPrice.toString(),
    })
  }

  const cancelEditing = (userId, messageType) => {
    const newEditing = { ...editingPricing }
    delete newEditing[`${userId}-${messageType}`]
    setEditingPricing(newEditing)
  }

  const saveEditing = (userId, messageType) => {
    const key = `${userId}-${messageType}`
    const price = editingPricing[key]
    if (price && !isNaN(Number.parseFloat(price))) {
      handleSetPricing(userId, messageType, price)
    }
  }

  const formatCredits = (value) => {
    const num = Number(value || 0)
    if (Number.isNaN(num)) return value || "0"
    return num.toFixed(5).replace(/\.0+$/, "").replace(/(\d\.\d*?[1-9])0+$/, "$1")
  }

  const formatDate = (date) => (date ? new Date(date).toLocaleString("en-IN") : "-")

  const filteredUsers = users
  const isReseller = user?.role === "reseller"

  if (!["superadmin", "admin", "reseller"].includes(user?.role)) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Access Denied</h1>
          <p className="text-gray-600 dark:text-gray-400">You don't have permission to access this page.</p>
        </div>
      </div>
    )
  }

  const handleOpenCountryModal = async (u) => {
    setCountryModalUser(u)
    setCountryModalLoading(true)
    setCountryModalSearch("")
    const res = await getUserCountryPricing(u.userId)
    setCountryModalLoading(false)
    if (res.success) {
      setCountryModalAllowInt(res.allowInternational || false)
      setCountryModalCountries(res.countries || [])
    } else {
      Swal.fire({ icon: "error", title: "Error", text: res.error || "Failed to load country rates" })
      setCountryModalUser(null)
    }
  }

  const handleSaveCountryModal = async () => {
    if (!countryModalUser) return
    const allowedCodes = countryModalCountries
      .filter((c) => c.isAllowed || c.countryCode === "91")
      .flatMap((c) => [c.countryCode, c.countryName])
    const countryPricingPayload = {}
    countryModalCountries.forEach((c) => {
      countryPricingPayload[c.countryCode] = c.rates
    })

    const payload = {
      allowInternational: countryModalAllowInt,
      allowedCountries: allowedCodes,
      countryPricing: countryPricingPayload,
    }

    const res = await setUserCountryPricing(countryModalUser.userId, payload)
    if (res.success) {
      Swal.fire({ icon: "success", title: "Saved", text: "Country rates & permissions updated for user", timer: 1500 })
      setCountryModalUser(null)
      fetchAllUsersPricing()
    } else {
      Swal.fire({ icon: "error", title: "Error", text: res.error || "Failed to save country rates" })
    }
  }

  const handleAddCustomCountrySubmit = async (e) => {
    e.preventDefault()
    if (!newCountryName.trim() || !newCountryCode.trim()) {
      Swal.fire({ icon: "warning", title: "Missing Fields", text: "Country Name and Calling Code are required" })
      return
    }

    const cleanCode = newCountryCode.trim().replace(/^\+/, "")

    const payload = {
      countryName: newCountryName.trim(),
      countryCode: cleanCode,
      marketing: Number.parseFloat(newMarketingDeduct) || 2.0,
      utility: Number.parseFloat(newUtilityDeduct) || 0.5,
      authentication: Number.parseFloat(newAuthDeduct) || 0.5,
    }

    const res = await addMasterCountry(payload)
    if (res.success) {
      const newCountryObj = {
        countryCode: cleanCode,
        countryName: newCountryName.trim(),
        isAllowed: true,
        status: "Enabled",
        hasCustomPricing: true,
        rates: {
          marketingDisplay: newMarketingDisplay || newMarketingDeduct || "2.0",
          marketingDeduct: newMarketingDeduct || "2.0",
          marketing: newMarketingDeduct || "2.0",
          utilityDisplay: newUtilityDisplay || newUtilityDeduct || "0.5",
          utilityDeduct: newUtilityDeduct || "0.5",
          utility: newUtilityDeduct || "0.5",
          authenticationDisplay: newAuthDisplay || newAuthDeduct || "0.5",
          authenticationDeduct: newAuthDeduct || "0.5",
          authentication: newAuthDeduct || "0.5",
        },
      }

      setCountryModalCountries((prev) => [newCountryObj, ...prev.filter((item) => item.countryCode !== cleanCode)])
      setShowAddCountryForm(false)
      setNewCountryName("")
      setNewCountryCode("")
      setNewMarketingDisplay("")
      setNewMarketingDeduct("")
      setNewUtilityDisplay("")
      setNewUtilityDeduct("")
      setNewAuthDisplay("")
      setNewAuthDeduct("")

      Swal.fire({ icon: "success", title: "Country Added!", text: `${newCountryName} (+${cleanCode}) added and enabled`, timer: 1500 })
    } else {
      Swal.fire({ icon: "error", title: "Error", text: res.error || "Failed to add country" })
    }
  }

  const handleEditCountry = (countryObj) => {
    setShowAddCountryForm(true)
    setNewCountryName(countryObj.countryName || "")
    setNewCountryCode(countryObj.countryCode || "")
    setNewMarketingDisplay(countryObj.rates?.marketingDisplay ?? countryObj.rates?.marketing ?? "2.0")
    setNewMarketingDeduct(countryObj.rates?.marketingDeduct ?? countryObj.rates?.marketing ?? "2.0")
    setNewUtilityDisplay(countryObj.rates?.utilityDisplay ?? countryObj.rates?.utility ?? "0.5")
    setNewUtilityDeduct(countryObj.rates?.utilityDeduct ?? countryObj.rates?.utility ?? "0.5")
    setNewAuthDisplay(countryObj.rates?.authenticationDisplay ?? countryObj.rates?.authentication ?? "0.5")
    setNewAuthDeduct(countryObj.rates?.authenticationDeduct ?? countryObj.rates?.authentication ?? "0.5")
  }

  const handleDeleteCountry = async (countryObj) => {
    const confirm = await Swal.fire({
      title: "Delete Country?",
      text: `Are you sure you want to delete ${countryObj.countryName} (+${countryObj.countryCode})?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, Delete It!",
    })

    if (confirm.isConfirmed) {
      const res = await deleteMasterCountry(countryObj.id || countryObj.countryCode)
      if (res.success) {
        setCountryModalCountries((prev) =>
          prev.filter((c) => (c.id || c.countryCode) !== (countryObj.id || countryObj.countryCode) && c.countryName !== countryObj.countryName)
        )
        Swal.fire({ icon: "success", title: "Deleted!", text: `${countryObj.countryName} removed successfully`, timer: 1500 })
      } else {
        Swal.fire({ icon: "error", title: "Error", text: res.error || "Failed to delete country" })
      }
    }
  }

  const handleToggleCountryStatus = async (countryObj, targetValue) => {
    const actionText = targetValue ? "enable" : "disable"
    const actionColor = targetValue ? "#16a34a" : "#dc2626"

    const confirm = await Swal.fire({
      title: `${targetValue ? "Enable" : "Disable"} ${countryObj.countryName}?`,
      text: `Are you sure you want to ${actionText} messaging permissions for ${countryObj.countryName} (+${countryObj.countryCode})?`,
      icon: targetValue ? "question" : "warning",
      showCancelButton: true,
      confirmButtonColor: actionColor,
      cancelButtonColor: "#6b7280",
      confirmButtonText: `Yes, ${targetValue ? "Enable" : "Disable"}`,
    })

    if (confirm.isConfirmed) {
      setCountryModalCountries((prev) =>
        prev.map((item) =>
          item.countryName === countryObj.countryName || item.countryCode === countryObj.countryCode
            ? { ...item, isAllowed: targetValue, status: targetValue ? "Enabled" : "Not Enabled" }
            : item
        )
      )
      Swal.fire({
        icon: targetValue ? "success" : "info",
        title: targetValue ? "Country Enabled" : "Country Disabled",
        text: `${countryObj.countryName} (+${countryObj.countryCode}) has been ${targetValue ? "enabled" : "disabled"}`,
        timer: 1500,
        toast: true,
        position: "top-end",
        showConfirmButton: false,
      })
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-yellow-50 dark:from-gray-900 dark:to-gray-800">
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-green-700 to-yellow-600 bg-clip-text text-transparent mb-2">
                Meta Pricing Management
              </h2>
              <p className="text-gray-600 dark:text-gray-400 text-lg">
                Set custom pricing for marketing, utility, and authentication messages
              </p>
            </div>
            {["superadmin", "admin"].includes(user?.role) && (
              <button
                type="button"
                onClick={() => navigate("/recharge")}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-green-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-green-700"
              >
                <BanknotesIcon className="h-5 w-5" />
                Balance
              </button>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="relative max-w-md">
            <MagnifyingGlassIcon className="w-5 h-5 absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value)
                setPage(1)
              }}
              className="pl-12 pr-4 py-2 w-full border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
            />
          </div>
          {!isReseller && (
            <button
              type="button"
              onClick={() => selectHistoryUser(null)}
              className={`px-4 py-2 rounded-xl border text-sm font-semibold transition-colors ${
                selectedUser
                  ? "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  : "bg-green-600 text-white border-green-600"
              }`}
            >
              All History
            </button>
          )}
        </div>

        {/* Default Pricing Info */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-5 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div>
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                Default Meta Rates (India Baseline: +91)
              </h3>
              <p className="text-xs text-blue-700 dark:text-blue-300">
                Automatic prefix detection applies exact Meta pricing per country calling code
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowCountryRates(!showCountryRates)}
              className="px-4 py-2 text-xs font-semibold rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors self-start sm:self-auto"
            >
              {showCountryRates ? "Hide Country Rates Table" : "View Country-Wise Rates (CSV Table)"}
            </button>
          </div>

          {/* India Primary Rate Overview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white/80 dark:bg-gray-800/80 rounded-xl p-3 text-center border border-blue-100 dark:border-blue-800/50">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                ₹{defaultPricing?.["91"]?.marketing ?? defaultPricing?.marketing ?? "1"}
              </div>
              <div className="text-sm font-medium text-blue-800 dark:text-blue-300">Marketing</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">India (+91)</div>
            </div>
            <div className="bg-white/80 dark:bg-gray-800/80 rounded-xl p-3 text-center border border-blue-100 dark:border-blue-800/50">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                ₹{defaultPricing?.["91"]?.utility ?? defaultPricing?.utility ?? "0.15"}
              </div>
              <div className="text-sm font-medium text-blue-800 dark:text-blue-300">Utility</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">India (+91)</div>
            </div>
            <div className="bg-white/80 dark:bg-gray-800/80 rounded-xl p-3 text-center border border-blue-100 dark:border-blue-800/50">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                ₹{defaultPricing?.["91"]?.authentication ?? defaultPricing?.authentication ?? "0.15"}
              </div>
              <div className="text-sm font-medium text-blue-800 dark:text-blue-300">Authentication</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">India (+91)</div>
            </div>
          </div>

          {/* Expandable Country Rates Table */}
          {showCountryRates && (
            <div className="mt-5 border-t border-blue-200 dark:border-blue-800 pt-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                <h4 className="font-semibold text-blue-900 dark:text-blue-100 text-sm">
                  Official Country-Wise Meta Rates (Effective July 2026)
                </h4>
                <input
                  type="text"
                  placeholder="Filter country or code..."
                  value={countryFilter}
                  onChange={(e) => setCountryFilter(e.target.value)}
                  className="px-3 py-1.5 text-xs border border-blue-300 dark:border-blue-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                />
              </div>

              <div className="max-h-60 overflow-y-auto rounded-lg border border-blue-200 dark:border-blue-800 bg-white dark:bg-gray-800">
                <table className="w-full text-xs text-left">
                  <thead className="bg-blue-100/60 dark:bg-blue-900/40 text-blue-900 dark:text-blue-200 sticky top-0">
                    <tr>
                      <th className="py-2 px-3">Country / Market</th>
                      <th className="py-2 px-3">Calling Code</th>
                      <th className="py-2 px-3">Marketing (₹)</th>
                      <th className="py-2 px-3">Utility (₹)</th>
                      <th className="py-2 px-3">Authentication (₹)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {Object.entries(
                      typeof defaultPricing === "object" && defaultPricing["91"]
                        ? defaultPricing
                        : {
                            "91": { name: "India", marketing: 0.8631, utility: 0.1150, authentication: 0.1150 },
                            "1": { name: "North America", marketing: 1.8313, utility: 0.2491, authentication: 0.2491 },
                            "44": { name: "United Kingdom", marketing: 4.6496, utility: 1.6122, authentication: 1.6122 },
                            "971": { name: "United Arab Emirates", marketing: 3.6613, utility: 1.1509, authentication: 1.1509 },
                            "966": { name: "Saudi Arabia", marketing: 3.6621, utility: 0.7834, authentication: 0.7834 },
                            "54": { name: "Argentina", marketing: 4.5293, utility: 1.9062, authentication: 1.9062 },
                            "55": { name: "Brazil", marketing: 4.5808, utility: 0.4984, authentication: 0.4984 },
                            "56": { name: "Chile", marketing: 6.5135, utility: 1.4654, authentication: 1.4654 },
                            "57": { name: "Colombia", marketing: 0.9161, utility: 0.0588, authentication: 0.0588 },
                            "20": { name: "Egypt", marketing: 4.7191, utility: 0.2639, authentication: 0.2639 },
                            "33": { name: "France", marketing: 6.2990, utility: 2.1994, authentication: 2.1994 },
                            "49": { name: "Germany", marketing: 10.0073, utility: 4.0322, authentication: 4.0322 },
                            "62": { name: "Indonesia", marketing: 3.0111, utility: 1.8316, authentication: 1.8316 },
                            "52": { name: "Mexico", marketing: 2.2357, utility: 0.6226, authentication: 0.6226 },
                            "92": { name: "Pakistan", marketing: 3.4683, utility: 0.7333, authentication: 0.7333 },
                            "other": { name: "Other / Default", marketing: 4.4248, utility: 0.5641, authentication: 0.5641 }
                          }
                    )
                      .filter(([code, item]) => {
                        const q = countryFilter.toLowerCase().trim()
                        if (!q) return true
                        return (
                          code.includes(q) ||
                          String(item.name || "").toLowerCase().includes(q)
                        )
                      })
                      .map(([code, item]) => (
                        <tr key={code} className="hover:bg-blue-50/50 dark:hover:bg-gray-700/50">
                          <td className="py-2 px-3 font-medium text-gray-900 dark:text-white">{item.name || code}</td>
                          <td className="py-2 px-3 font-mono text-blue-700 dark:text-blue-300">+{code}</td>
                          <td className="py-2 px-3 font-semibold text-gray-900 dark:text-white">₹{item.marketing}</td>
                          <td className="py-2 px-3 text-gray-700 dark:text-gray-300">₹{item.utility}</td>
                          <td className="py-2 px-3 text-gray-700 dark:text-gray-300">₹{item.authentication}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Users Table */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="text-left py-4 px-6 text-gray-900 dark:text-white font-semibold">User</th>
                  <th className="text-left py-4 px-6 text-gray-900 dark:text-white font-semibold">Role</th>
                  <th className="text-left py-4 px-6 text-gray-900 dark:text-white font-semibold">Country</th>
                  <th className="text-left py-4 px-6 text-gray-900 dark:text-white font-semibold">Marketing (₹)</th>
                  <th className="text-left py-4 px-6 text-gray-900 dark:text-white font-semibold">Utility (₹)</th>
                  <th className="text-left py-4 px-6 text-gray-900 dark:text-white font-semibold">Authentication (₹)</th>
                  <th className="text-left py-4 px-6 text-gray-900 dark:text-white font-semibold">Custom Pricing</th>
                  {!isReseller && (
                    <>
                      <th className="text-left py-4 px-6 text-gray-900 dark:text-white font-semibold">Deduct On Failed</th>
                      <th className="text-left py-4 px-6 text-gray-900 dark:text-white font-semibold">Show Credits</th>
                      <th className="text-left py-4 px-6 text-gray-900 dark:text-white font-semibold">Failed Cap Enabled</th>
                      <th className="text-left py-4 px-6 text-gray-900 dark:text-white font-semibold">Failed Cap %</th>
                      <th className="text-left py-4 px-6 text-gray-900 dark:text-white font-semibold">Refund Failed Credit</th>
                      <th className="text-left py-4 px-6 text-gray-900 dark:text-white font-semibold">Refund Delay (hrs)</th>
                      <th className="text-left py-4 px-6 text-gray-900 dark:text-white font-semibold">History</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {loading ? (
                  <tr>
                    <td colSpan={isReseller ? "7" : "13"} className="py-12 text-center">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
                      </div>
                    </td>
                  </tr>
                ) : filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={isReseller ? "7" : "13"} className="py-12 text-center text-gray-500 dark:text-gray-400">
                      <UserIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p className="text-lg font-medium">No users found</p>
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((u) => {
                    const defaultIndiaRates = { marketing: 1, utility: 0.15, authentication: 0.15 }
                    const defaultMarketing = defaultPricing?.["91"]?.marketing ?? defaultPricing?.marketing ?? defaultIndiaRates.marketing
                    const defaultUtility = defaultPricing?.["91"]?.utility ?? defaultPricing?.utility ?? defaultIndiaRates.utility
                    const defaultAuth = defaultPricing?.["91"]?.authentication ?? defaultPricing?.authentication ?? defaultIndiaRates.authentication

                    const ratesList =
                      u.enabledCountryRates && u.enabledCountryRates.length > 0
                        ? u.enabledCountryRates
                        : [
                            {
                              countryCode: "91",
                              countryName: "India",
                              marketing: u.pricing?.marketing ?? defaultMarketing,
                              utility: u.pricing?.utility ?? defaultUtility,
                              authentication: u.pricing?.authentication ?? defaultAuth,
                            },
                          ]

                    const rowSpan = ratesList.length

                    return (
                      <tr
                        key={u.userId}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-200"
                      >
                        <td className="py-4 px-6 align-top border-r border-gray-100 dark:border-gray-800">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-yellow-500 rounded-xl flex items-center justify-center mr-3 shadow-lg flex-shrink-0">
                              <UserIcon className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <div className="font-semibold text-gray-900 dark:text-white">{u.name || "N/A"}</div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">{u.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-6 align-top border-r border-gray-100 dark:border-gray-800">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              u.role === "superadmin"
                                ? "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200"
                                : u.role === "admin"
                                  ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                                  : u.role === "reseller"
                                    ? "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200"
                                    : u.role === "company"
                                      ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                                      : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                            }`}
                          >
                            {u.role.charAt(0).toUpperCase() + u.role.slice(1)}
                          </span>
                        </td>

                        {/* Country Rates Container (Max 2 rows visible, scrollable if more) */}
                        <td colSpan={4} className="py-3 px-4 align-top border-r border-gray-100 dark:border-gray-800">
                          <div className="max-h-[82px] overflow-y-auto pr-1 space-y-2">
                            {ratesList.map((c, i) => (
                              <div
                                key={`${u.userId}-${c.countryCode}-${i}`}
                                className="grid grid-cols-4 gap-2 items-center text-xs pb-1 border-b border-gray-100 dark:border-gray-800/60 last:border-0 last:pb-0"
                              >
                                <div className="font-mono text-xs font-medium text-blue-600 dark:text-blue-400 truncate">
                                  +{c.countryCode} ({c.countryName})
                                </div>
                                <div className="font-medium text-gray-900 dark:text-white">
                                  ₹{c.marketing}
                                </div>
                                <div className="font-medium text-gray-900 dark:text-white">
                                  ₹{c.utility}
                                </div>
                                <div className="font-medium text-gray-900 dark:text-white">
                                  ₹{c.authentication}
                                </div>
                              </div>
                            ))}
                          </div>
                        </td>

                        <td className="py-4 px-6 align-top border-l border-gray-100 dark:border-gray-800">
                              <div className="flex flex-col gap-1 items-start">
                                <span
                                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                    u.hasCustomPricing
                                      ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                      : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                                  }`}
                                >
                                  {u.hasCustomPricing ? "Custom Rates" : "Standard"}
                                </span>
                                {!isReseller && (
                                  <button
                                    type="button"
                                    onClick={() => handleOpenCountryModal(u)}
                                    className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
                                  >
                                    Manage Country Rates
                                  </button>
                                )}
                              </div>
                            </td>
                            {!isReseller && (
                              <>
                                <td className="py-4 px-6 align-top">
                                  <label className="inline-flex items-center cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={u.billing?.deductOnFailed ?? true}
                                      onChange={async (e) => {
                                        const newVal = e.target.checked
                                        const oldBilling = u.billing || { deductOnFailed: true, showCredits: true }
                                        u.billing = { ...oldBilling, deductOnFailed: newVal }
                                        setUsers((prev) => prev.map((p) => (p.userId === u.userId ? { ...p, billing: u.billing } : p)))
                                        const res = await setUserBilling(u.userId, { deductOnFailed: newVal })
                                        if (!res.success) {
                                          Swal.fire({ icon: "error", title: "Error", text: res.error || "Failed to update setting", confirmButtonColor: "#4A7C59" })
                                          u.billing = oldBilling
                                          setUsers((prev) => prev.map((p) => (p.userId === u.userId ? { ...p, billing: oldBilling } : p)))
                                        }
                                      }}
                                      className="form-checkbox h-4 w-4 text-green-600"
                                    />
                                  </label>
                                </td>
                                <td className="py-4 px-6 align-top">
                                  <label className="inline-flex items-center cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={u.billing?.showCredits ?? true}
                                      onChange={async (e) => {
                                        const newVal = e.target.checked
                                        const oldBilling = u.billing || { deductOnFailed: true, showCredits: true }
                                        u.billing = { ...oldBilling, showCredits: newVal }
                                        setUsers((prev) => prev.map((p) => (p.userId === u.userId ? { ...p, billing: u.billing } : p)))
                                        const res = await setUserBilling(u.userId, { showCredits: newVal })
                                        if (!res.success) {
                                          Swal.fire({ icon: "error", title: "Error", text: res.error || "Failed to update setting", confirmButtonColor: "#4A7C59" })
                                          u.billing = oldBilling
                                          setUsers((prev) => prev.map((p) => (p.userId === u.userId ? { ...p, billing: oldBilling } : p)))
                                        }
                                      }}
                                      className="form-checkbox h-4 w-4 text-green-600"
                                    />
                                  </label>
                                </td>
                                <td className="py-4 px-6 align-top">
                                  <label className="inline-flex items-center cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={u.reporting?.failedCapEnabled ?? false}
                                      onChange={async (e) => {
                                        const newVal = e.target.checked
                                        const oldReporting = u.reporting || { failedCapEnabled: false, failedCapPercent: 0 }
                                        u.reporting = { ...oldReporting, failedCapEnabled: newVal }
                                        setUsers((prev) => prev.map((p) => (p.userId === u.userId ? { ...p, reporting: u.reporting } : p)))
                                        const res = await setUserBilling(u.userId, { failedCapEnabled: newVal })
                                        if (!res.success) {
                                          Swal.fire({ icon: "error", title: "Error", text: res.error || "Failed to update setting", confirmButtonColor: "#4A7C59" })
                                          u.reporting = oldReporting
                                          setUsers((prev) => prev.map((p) => (p.userId === u.userId ? { ...p, reporting: oldReporting } : p)))
                                        }
                                      }}
                                      className="form-checkbox h-4 w-4 text-green-600"
                                    />
                                  </label>
                                </td>
                                <td className="py-4 px-6 align-top">
                                  <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="1"
                                    value={u.reporting?.failedCapPercent ?? 0}
                                    onChange={(e) => {
                                      const val = Number.parseInt(e.target.value || 0)
                                      const oldReporting = u.reporting || { failedCapEnabled: false, failedCapPercent: 0 }
                                      u.reporting = { ...oldReporting, failedCapPercent: isNaN(val) ? 0 : val }
                                      setUsers((prev) => prev.map((p) => (p.userId === u.userId ? { ...p, reporting: u.reporting } : p)))
                                    }}
                                    onBlur={async (e) => {
                                      const val = Number.parseInt(e.target.value || 0)
                                      const sanitized = isNaN(val) ? 0 : Math.max(0, Math.min(100, val))
                                      const oldReporting = u.reporting || { failedCapEnabled: false, failedCapPercent: 0 }
                                      u.reporting = { ...oldReporting, failedCapPercent: sanitized }
                                      setUsers((prev) => prev.map((p) => (p.userId === u.userId ? { ...p, reporting: u.reporting } : p)))
                                      const res = await setUserBilling(u.userId, { failedCapPercent: sanitized })
                                      if (!res.success) {
                                        Swal.fire({ icon: "error", title: "Error", text: res.error || "Failed to update setting", confirmButtonColor: "#4A7C59" })
                                        u.reporting = oldReporting
                                        setUsers((prev) => prev.map((p) => (p.userId === u.userId ? { ...p, reporting: oldReporting } : p)))
                                      }
                                    }}
                                    className="w-20 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500"
                                  />
                                </td>
                                <td className="py-4 px-6 align-top">
                                  <label className="inline-flex items-center cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={u.billing?.failedRefundEnabled ?? false}
                                      onChange={async (e) => {
                                        const newVal = e.target.checked
                                        const oldBilling = u.billing || { deductOnFailed: true, showCredits: true, failedRefundEnabled: false, failedRefundHours: 24 }
                                        u.billing = { ...oldBilling, failedRefundEnabled: newVal }
                                        setUsers((prev) => prev.map((p) => (p.userId === u.userId ? { ...p, billing: u.billing } : p)))
                                        const res = await setUserBilling(u.userId, { failedRefundEnabled: newVal })
                                        if (!res.success) {
                                          Swal.fire({ icon: "error", title: "Error", text: res.error || "Failed to update setting", confirmButtonColor: "#4A7C59" })
                                          u.billing = oldBilling
                                          setUsers((prev) => prev.map((p) => (p.userId === u.userId ? { ...p, billing: oldBilling } : p)))
                                        }
                                      }}
                                      className="form-checkbox h-4 w-4 text-green-600"
                                    />
                                  </label>
                                </td>
                                <td className="py-4 px-6 align-top">
                                  <input
                                    type="number"
                                    min="1"
                                    max="168"
                                    step="1"
                                    value={u.billing?.failedRefundHours ?? 24}
                                    onChange={(e) => {
                                      const val = Number.parseInt(e.target.value || 24)
                                      const oldBilling = u.billing || { deductOnFailed: true, showCredits: true, failedRefundEnabled: false, failedRefundHours: 24 }
                                      u.billing = { ...oldBilling, failedRefundHours: isNaN(val) ? 24 : val }
                                      setUsers((prev) => prev.map((p) => (p.userId === u.userId ? { ...p, billing: u.billing } : p)))
                                    }}
                                    onBlur={async (e) => {
                                      const val = Number.parseInt(e.target.value || 24)
                                      const sanitized = isNaN(val) ? 24 : Math.max(1, Math.min(168, val))
                                      const oldBilling = u.billing || { deductOnFailed: true, showCredits: true, failedRefundEnabled: false, failedRefundHours: 24 }
                                      u.billing = { ...oldBilling, failedRefundHours: sanitized }
                                      setUsers((prev) => prev.map((p) => (p.userId === u.userId ? { ...p, billing: oldBilling } : p)))
                                      const res = await setUserBilling(u.userId, { failedRefundHours: sanitized })
                                      if (!res.success) {
                                        Swal.fire({ icon: "error", title: "Error", text: res.error || "Failed to update setting", confirmButtonColor: "#4A7C59" })
                                        u.billing = oldBilling
                                        setUsers((prev) => prev.map((p) => (p.userId === u.userId ? { ...p, billing: oldBilling } : p)))
                                      }
                                    }}
                                    className="w-20 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500"
                                  />
                                </td>
                                <td className="py-4 px-6 align-top">
                                  <button
                                    type="button"
                                    onClick={() => selectHistoryUser(u)}
                                    className={`px-3 py-1 rounded-lg text-xs font-semibold border transition-colors ${
                                      selectedUser?.userId === u.userId
                                        ? "bg-green-600 text-white border-green-600"
                                        : "bg-white text-green-700 border-green-200 hover:bg-green-50"
                                    }`}
                                  >
                                    View
                                  </button>
                                </td>
                              </>
                            )}
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
          {/* Pagination controls */}
          <div className="flex items-center justify-between p-4 border-t border-gray-100 dark:border-gray-700">
            <div className="text-sm text-gray-600">Page {pagination.currentPage} of {pagination.totalPages}</div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={pagination.currentPage <= 1}
                className="px-3 py-1 bg-white border rounded disabled:opacity-50"
              >Prev</button>
              <button
                onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                disabled={pagination.currentPage >= pagination.totalPages}
                className="px-3 py-1 bg-white border rounded disabled:opacity-50"
              >Next</button>
            </div>
          </div>
        </div>

        {!isReseller && (
          <div className="mt-6 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="p-4 border-b border-gray-100 dark:border-gray-700 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                {selectedUser ? `${selectedUser.name || selectedUser.email || "User"} History` : "All Users History"}
              </h3>
            </div>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div className="px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-700">
                <p className="text-xs text-gray-500 dark:text-gray-300">Charged</p>
                <p className="font-bold text-gray-900 dark:text-white">Rs. {formatCredits(historySummary.totalCharged || 0)}</p>
              </div>
              <div className="px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20">
                <p className="text-xs text-amber-700 dark:text-amber-300">Refunded</p>
                <p className="font-bold text-amber-800 dark:text-amber-200">Rs. {formatCredits(historySummary.totalRefunded || 0)}</p>
              </div>
              <div className="px-3 py-2 rounded-lg bg-green-50 dark:bg-green-900/20">
                <p className="text-xs text-green-700 dark:text-green-300">Refund Count</p>
                <p className="font-bold text-green-800 dark:text-green-200">{historySummary.refundCount || 0}</p>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="text-left py-3 px-4 text-gray-900 dark:text-white font-semibold">Date & Time</th>
                  {!selectedUser && <th className="text-left py-3 px-4 text-gray-900 dark:text-white font-semibold">User</th>}
                  <th className="text-left py-3 px-4 text-gray-900 dark:text-white font-semibold">Campaign</th>
                  <th className="text-left py-3 px-4 text-gray-900 dark:text-white font-semibold">Recipient</th>
                  <th className="text-left py-3 px-4 text-gray-900 dark:text-white font-semibold">Status</th>
                  <th className="text-left py-3 px-4 text-gray-900 dark:text-white font-semibold">Campaign Cut</th>
                  <th className="text-left py-3 px-4 text-gray-900 dark:text-white font-semibold">Campaign Balance</th>
                  <th className="text-left py-3 px-4 text-gray-900 dark:text-white font-semibold">Refund</th>
                  <th className="text-left py-3 px-4 text-gray-900 dark:text-white font-semibold">After Refund</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {historyLoading ? (
                  <tr>
                    <td colSpan={selectedUser ? 8 : 9} className="py-10 text-center">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                      </div>
                    </td>
                  </tr>
                ) : history.length === 0 ? (
                  <tr>
                    <td colSpan={selectedUser ? 8 : 9} className="py-10 text-center text-gray-500 dark:text-gray-400">
                      No history found
                    </td>
                  </tr>
                ) : (
                  history.map((record) => (
                    <tr key={record.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="py-3 px-4 whitespace-nowrap text-gray-900 dark:text-white">{formatDate(record.createdAt)}</td>
                      {!selectedUser && (
                        <td className="py-3 px-4">
                          <div className="font-medium text-gray-900 dark:text-white">{record.userName || "N/A"}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">{record.userEmail || ""}</div>
                        </td>
                      )}
                      <td className="py-3 px-4">
                        <div className="font-medium text-gray-900 dark:text-white">{record.campaignName || "N/A"}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{record.templateName || ""}</div>
                      </td>
                      <td className="py-3 px-4 font-mono text-gray-900 dark:text-white">{record.recipientNumber || "-"}</td>
                      <td className="py-3 px-4">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            record.status === "refunded"
                              ? "bg-amber-100 text-amber-800"
                              : record.status === "failed"
                                ? "bg-red-100 text-red-800"
                                : "bg-green-100 text-green-800"
                          }`}
                        >
                          {record.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 font-semibold text-gray-900 dark:text-white">Rs. {formatCredits(record.chargeAmount || 0)}</td>
                      <td className="py-3 px-4 text-xs text-gray-700 dark:text-gray-300">
                        {record.campaignBalanceBefore !== null && record.campaignBalanceBefore !== undefined ? (
                          <>
                            <div>Old: Rs. {formatCredits(record.campaignBalanceBefore)}</div>
                            <div>After: Rs. {formatCredits(record.campaignBalanceAfter)}</div>
                          </>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="py-3 px-4 font-semibold text-amber-700 dark:text-amber-300">
                        {record.refundAmount ? `Rs. ${formatCredits(record.refundAmount)}` : "-"}
                      </td>
                      <td className="py-3 px-4 text-xs text-gray-700 dark:text-gray-300">
                        {record.status === "refunded" ? (
                          <>
                            <div>Before: Rs. {formatCredits(record.refundBalanceBefore)}</div>
                            <div>After: Rs. {formatCredits(record.refundBalanceAfter)}</div>
                          </>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between p-4 border-t border-gray-100 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Page {historyPagination.currentPage || historyPage} of {historyPagination.totalPages || 1}
              {historyPagination.totalRecords ? ` (${historyPagination.totalRecords} records)` : ""}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                disabled={(historyPagination.currentPage || historyPage) <= 1}
                className="px-3 py-1 bg-white border rounded disabled:opacity-50"
              >
                Prev
              </button>
              <button
                type="button"
                onClick={() => setHistoryPage((p) => Math.min(historyPagination.totalPages || 1, p + 1))}
                disabled={(historyPagination.currentPage || historyPage) >= (historyPagination.totalPages || 1)}
                className="px-3 py-1 bg-white border rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
        )}

        {/* Per-User Country Pricing & Permissions Modal */}
        {countryModalUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm overflow-y-auto">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
              {/* Modal Header */}
              <div className="p-5 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-700/50">
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                    Country Rates & Permissions: {countryModalUser.name || countryModalUser.username}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Enable/disable specific country prefixes and set custom pricing rates (INR)
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setCountryModalUser(null)}
                  className="p-1 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-5 overflow-y-auto flex-1 space-y-4">
                {/* Global Toggle & Search */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="modalAllowAllInt"
                      checked={countryModalAllowInt}
                      onChange={(e) => setCountryModalAllowInt(e.target.checked)}
                      className="form-checkbox h-5 w-5 text-blue-600 rounded"
                    />
                    <label htmlFor="modalAllowAllInt" className="cursor-pointer">
                      <span className="text-sm font-semibold text-blue-900 dark:text-blue-100 block">
                        Allow All International Destinations
                      </span>
                      <span className="text-xs text-blue-700 dark:text-blue-300">
                        When enabled, all non-India numbers are permitted automatically
                      </span>
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setShowAddCountryForm(!showAddCountryForm)}
                      className="px-3 py-1.5 text-xs font-semibold text-white bg-green-600 hover:bg-green-700 rounded-lg shadow-sm flex items-center gap-1 transition-colors"
                    >
                      <span className="text-sm font-bold">+</span> Add Country
                    </button>
                    <input
                      type="text"
                      placeholder="Search country or code..."
                      value={countryModalSearch}
                      onChange={(e) => setCountryModalSearch(e.target.value)}
                      className="px-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white max-w-xs"
                    />
                  </div>
                </div>

                {/* Add Country Form Card */}
                {showAddCountryForm && (
                  <form onSubmit={handleAddCustomCountrySubmit} className="bg-green-50/80 dark:bg-green-900/20 p-4 rounded-xl border border-green-200 dark:border-green-800 space-y-3">
                    <div className="flex items-center justify-between border-b border-green-200 dark:border-green-800 pb-2">
                      <h4 className="text-xs font-bold text-green-900 dark:text-green-100">
                        + Add Custom Country Prefix & Rates
                      </h4>
                      <button
                        type="button"
                        onClick={() => setShowAddCountryForm(false)}
                        className="text-xs text-gray-500 hover:text-gray-700"
                      >
                        ✕ Close
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[11px] font-semibold text-gray-700 dark:text-gray-300 mb-1">
                          Country Name *
                        </label>
                        <input
                          type="text"
                          required
                          placeholder="e.g. Sri Lanka"
                          value={newCountryName}
                          onChange={(e) => setNewCountryName(e.target.value)}
                          className="w-full px-2.5 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-semibold text-gray-700 dark:text-gray-300 mb-1">
                          Country Calling Code (Prefix) *
                        </label>
                        <input
                          type="text"
                          required
                          placeholder="e.g. 94"
                          value={newCountryCode}
                          onChange={(e) => setNewCountryCode(e.target.value)}
                          className="w-full px-2.5 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 font-mono"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {/* Marketing Rates */}
                      <div className="bg-white dark:bg-gray-800 p-2.5 rounded-lg border border-gray-200 dark:border-gray-700">
                        <span className="block text-[11px] font-bold text-gray-800 dark:text-gray-200 mb-1.5">
                          Marketing Rate (₹)
                        </span>
                        <div className="grid grid-cols-2 gap-1.5">
                          <div>
                            <span className="block text-[9px] text-gray-500">Display</span>
                            <input
                              type="number"
                              step="0.0001"
                              placeholder="2.00"
                              value={newMarketingDisplay}
                              onChange={(e) => setNewMarketingDisplay(e.target.value)}
                              className="w-full px-1.5 py-1 border text-xs rounded font-mono"
                            />
                          </div>
                          <div>
                            <span className="block text-[9px] text-gray-500">Deduct</span>
                            <input
                              type="number"
                              step="0.0001"
                              placeholder="2.00"
                              value={newMarketingDeduct}
                              onChange={(e) => setNewMarketingDeduct(e.target.value)}
                              className="w-full px-1.5 py-1 border border-green-300 text-xs rounded font-mono bg-green-50/50"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Utility Rates */}
                      <div className="bg-white dark:bg-gray-800 p-2.5 rounded-lg border border-gray-200 dark:border-gray-700">
                        <span className="block text-[11px] font-bold text-gray-800 dark:text-gray-200 mb-1.5">
                          Utility Rate (₹)
                        </span>
                        <div className="grid grid-cols-2 gap-1.5">
                          <div>
                            <span className="block text-[9px] text-gray-500">Display</span>
                            <input
                              type="number"
                              step="0.0001"
                              placeholder="0.50"
                              value={newUtilityDisplay}
                              onChange={(e) => setNewUtilityDisplay(e.target.value)}
                              className="w-full px-1.5 py-1 border text-xs rounded font-mono"
                            />
                          </div>
                          <div>
                            <span className="block text-[9px] text-gray-500">Deduct</span>
                            <input
                              type="number"
                              step="0.0001"
                              placeholder="0.50"
                              value={newUtilityDeduct}
                              onChange={(e) => setNewUtilityDeduct(e.target.value)}
                              className="w-full px-1.5 py-1 border border-green-300 text-xs rounded font-mono bg-green-50/50"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Auth Rates */}
                      <div className="bg-white dark:bg-gray-800 p-2.5 rounded-lg border border-gray-200 dark:border-gray-700">
                        <span className="block text-[11px] font-bold text-gray-800 dark:text-gray-200 mb-1.5">
                          Authentication Rate (₹)
                        </span>
                        <div className="grid grid-cols-2 gap-1.5">
                          <div>
                            <span className="block text-[9px] text-gray-500">Display</span>
                            <input
                              type="number"
                              step="0.0001"
                              placeholder="0.50"
                              value={newAuthDisplay}
                              onChange={(e) => setNewAuthDisplay(e.target.value)}
                              className="w-full px-1.5 py-1 border text-xs rounded font-mono"
                            />
                          </div>
                          <div>
                            <span className="block text-[9px] text-gray-500">Deduct</span>
                            <input
                              type="number"
                              step="0.0001"
                              placeholder="0.50"
                              value={newAuthDeduct}
                              onChange={(e) => setNewAuthDeduct(e.target.value)}
                              className="w-full px-1.5 py-1 border border-green-300 text-xs rounded font-mono bg-green-50/50"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end gap-2 pt-1">
                      <button
                        type="button"
                        onClick={() => setShowAddCountryForm(false)}
                        className="px-3 py-1 text-xs text-gray-600 bg-white border rounded"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-3 py-1 text-xs text-white bg-green-600 hover:bg-green-700 rounded font-semibold"
                      >
                        Save & Enable Country
                      </button>
                    </div>
                  </form>
                )}

                {countryModalLoading ? (
                  <div className="py-12 text-center text-gray-500">Loading country rates...</div>
                ) : (
                  <div className="space-y-6">
                    {/* 1. ENABLED COUNTRIES SECTION */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full bg-green-500"></span>
                          Enabled Countries
                        </h4>
                        <span className="text-xs text-gray-500">
                          {countryModalCountries.filter((c) => c.countryCode === "91" || c.isAllowed || countryModalAllowInt).length} Enabled
                        </span>
                      </div>
                      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                        <table className="w-full text-xs text-left">
                          <thead className="bg-green-50 dark:bg-green-900/20 text-gray-700 dark:text-gray-300 sticky top-0 border-b border-green-100 dark:border-green-800">
                            <tr>
                              <th className="py-2.5 px-3">Enabled</th>
                              <th className="py-2.5 px-3">Country</th>
                              <th className="py-2.5 px-3">Code</th>
                              <th className="py-2.5 px-2 text-center" colSpan={2}>
                                Marketing (₹)
                                <div className="flex justify-around text-[10px] font-normal text-gray-500">
                                  <span>Display</span>
                                  <span>Deduct</span>
                                </div>
                              </th>
                              <th className="py-2.5 px-2 text-center" colSpan={2}>
                                Utility (₹)
                                <div className="flex justify-around text-[10px] font-normal text-gray-500">
                                  <span>Display</span>
                                  <span>Deduct</span>
                                </div>
                              </th>
                              <th className="py-2.5 px-2 text-center" colSpan={2}>
                                Authentication (₹)
                                <div className="flex justify-around text-[10px] font-normal text-gray-500">
                                  <span>Display</span>
                                  <span>Deduct</span>
                                </div>
                              </th>
                              <th className="py-2.5 px-3">Status</th>
                              <th className="py-2.5 px-3 text-center">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {countryModalCountries
                              .filter((c) => c.countryCode === "91" || c.isAllowed || countryModalAllowInt)
                              .filter((c) => {
                                const q = countryModalSearch.toLowerCase().trim()
                                if (!q) return true
                                return c.countryCode.includes(q) || String(c.countryName || "").toLowerCase().includes(q)
                              })
                              .map((c) => (
                                <tr key={c.id || c.countryName || c.countryCode} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                  <td className="py-2.5 px-3">
                                    <input
                                      type="checkbox"
                                      disabled={c.countryCode === "91"}
                                      checked={true}
                                      onChange={() => handleToggleCountryStatus(c, false)}
                                      className="form-checkbox h-4 w-4 text-green-600 rounded disabled:opacity-50 cursor-pointer"
                                    />
                                  </td>
                                  <td className="py-2.5 px-3 font-medium text-gray-900 dark:text-white">
                                    {c.countryName}
                                    {c.countryCode === "91" && <span className="ml-1 text-[10px] text-green-600 font-bold">(Default)</span>}
                                  </td>
                                  <td className="py-2.5 px-3 font-mono text-blue-600 dark:text-blue-400">+{c.countryCode}</td>
                                  
                                  {/* Marketing Display & Deduct */}
                                  <td className="py-2.5 px-1">
                                    <input
                                      type="number"
                                      step="0.0001"
                                      placeholder="Display"
                                      title="Display Price"
                                      value={c.rates?.marketingDisplay ?? c.rates?.marketing ?? ""}
                                      onChange={(e) => {
                                        const val = e.target.value
                                        setCountryModalCountries((prev) =>
                                          prev.map((item) =>
                                            item.countryName === c.countryName || item.countryCode === c.countryCode
                                              ? { ...item, rates: { ...item.rates, marketingDisplay: val, marketing: item.rates?.marketingDeduct ?? val } }
                                              : item
                                          )
                                        )
                                      }}
                                      className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-white font-mono"
                                    />
                                  </td>
                                  <td className="py-2.5 px-1">
                                    <input
                                      type="number"
                                      step="0.0001"
                                      placeholder="Deduct"
                                      title="Deduct Price"
                                      value={c.rates?.marketingDeduct ?? c.rates?.marketing ?? ""}
                                      onChange={(e) => {
                                        const val = e.target.value
                                        setCountryModalCountries((prev) =>
                                          prev.map((item) =>
                                            item.countryName === c.countryName || item.countryCode === c.countryCode
                                              ? { ...item, rates: { ...item.rates, marketingDeduct: val, marketing: val } }
                                              : item
                                          )
                                        )
                                      }}
                                      className="w-20 px-2 py-1 border border-green-300 dark:border-green-600 rounded text-xs bg-green-50/50 dark:bg-gray-800 text-gray-900 dark:text-white font-mono font-medium"
                                    />
                                  </td>

                                  {/* Utility Display & Deduct */}
                                  <td className="py-2.5 px-1">
                                    <input
                                      type="number"
                                      step="0.0001"
                                      placeholder="Display"
                                      title="Display Price"
                                      value={c.rates?.utilityDisplay ?? c.rates?.utility ?? ""}
                                      onChange={(e) => {
                                        const val = e.target.value
                                        setCountryModalCountries((prev) =>
                                          prev.map((item) =>
                                            item.countryName === c.countryName || item.countryCode === c.countryCode
                                              ? { ...item, rates: { ...item.rates, utilityDisplay: val, utility: item.rates?.utilityDeduct ?? val } }
                                              : item
                                          )
                                        )
                                      }}
                                      className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-white font-mono"
                                    />
                                  </td>
                                  <td className="py-2.5 px-1">
                                    <input
                                      type="number"
                                      step="0.0001"
                                      placeholder="Deduct"
                                      title="Deduct Price"
                                      value={c.rates?.utilityDeduct ?? c.rates?.utility ?? ""}
                                      onChange={(e) => {
                                        const val = e.target.value
                                        setCountryModalCountries((prev) =>
                                          prev.map((item) =>
                                            item.countryName === c.countryName || item.countryCode === c.countryCode
                                              ? { ...item, rates: { ...item.rates, utilityDeduct: val, utility: val } }
                                              : item
                                          )
                                        )
                                      }}
                                      className="w-20 px-2 py-1 border border-green-300 dark:border-green-600 rounded text-xs bg-green-50/50 dark:bg-gray-800 text-gray-900 dark:text-white font-mono font-medium"
                                    />
                                  </td>

                                  {/* Auth Display & Deduct */}
                                  <td className="py-2.5 px-1">
                                    <input
                                      type="number"
                                      step="0.0001"
                                      placeholder="Display"
                                      title="Display Price"
                                      value={c.rates?.authenticationDisplay ?? c.rates?.authentication ?? ""}
                                      onChange={(e) => {
                                        const val = e.target.value
                                        setCountryModalCountries((prev) =>
                                          prev.map((item) =>
                                            item.countryName === c.countryName || item.countryCode === c.countryCode
                                              ? { ...item, rates: { ...item.rates, authenticationDisplay: val, authentication: item.rates?.authenticationDeduct ?? val } }
                                              : item
                                          )
                                        )
                                      }}
                                      className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-white font-mono"
                                    />
                                  </td>
                                  <td className="py-2.5 px-1">
                                    <input
                                      type="number"
                                      step="0.0001"
                                      placeholder="Deduct"
                                      title="Deduct Price"
                                      value={c.rates?.authenticationDeduct ?? c.rates?.authentication ?? ""}
                                      onChange={(e) => {
                                        const val = e.target.value
                                        setCountryModalCountries((prev) =>
                                          prev.map((item) =>
                                            item.countryName === c.countryName || item.countryCode === c.countryCode
                                              ? { ...item, rates: { ...item.rates, authenticationDeduct: val, authentication: val } }
                                              : item
                                          )
                                        )
                                      }}
                                      className="w-20 px-2 py-1 border border-green-300 dark:border-green-600 rounded text-xs bg-green-50/50 dark:bg-gray-800 text-gray-900 dark:text-white font-mono font-medium"
                                    />
                                  </td>

                                  <td className="py-2.5 px-3">
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">
                                      Enabled
                                    </span>
                                  </td>
                                  <td className="py-2.5 px-3 text-center">
                                    <div className="flex items-center justify-center gap-1.5">
                                      <button
                                        type="button"
                                        title="Edit Country"
                                        onClick={() => handleEditCountry(c)}
                                        className="p-1 text-blue-600 hover:text-blue-800 dark:text-blue-400"
                                      >
                                        <PencilIcon className="w-4 h-4" />
                                      </button>
                                      {c.isCustom && (
                                        <button
                                          type="button"
                                          title="Delete Country"
                                          onClick={() => handleDeleteCountry(c)}
                                          className="p-1 text-red-600 hover:text-red-800 dark:text-red-400"
                                        >
                                          <TrashIcon className="w-4 h-4" />
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* 2. DISABLED COUNTRIES SECTION */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full bg-gray-400"></span>
                          Other Countries (Not Enabled)
                        </h4>
                        <span className="text-xs text-gray-500">
                          {countryModalCountries.filter((c) => c.countryCode !== "91" && !c.isAllowed && !countryModalAllowInt).length} Countries
                        </span>
                      </div>
                      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                        <table className="w-full text-xs text-left">
                          <thead className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 sticky top-0">
                            <tr>
                              <th className="py-2.5 px-3">Enable</th>
                              <th className="py-2.5 px-3">Country Name</th>
                              <th className="py-2.5 px-3">Country Code</th>
                              <th className="py-2.5 px-3">Status</th>
                              <th className="py-2.5 px-3 text-center">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {countryModalCountries
                              .filter((c) => c.countryCode !== "91" && !c.isAllowed && !countryModalAllowInt)
                              .filter((c) => {
                                const q = countryModalSearch.toLowerCase().trim()
                                if (!q) return true
                                return c.countryCode.includes(q) || String(c.countryName || "").toLowerCase().includes(q)
                              })
                              .map((c) => (
                                <tr key={c.id || c.countryName || c.countryCode} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                  <td className="py-2.5 px-3">
                                    <input
                                      type="checkbox"
                                      checked={false}
                                      onChange={() => handleToggleCountryStatus(c, true)}
                                      className="form-checkbox h-4 w-4 text-green-600 rounded cursor-pointer"
                                    />
                                  </td>
                                  <td className="py-2.5 px-3 font-medium text-gray-700 dark:text-gray-300">
                                    {c.countryName}
                                  </td>
                                  <td className="py-2.5 px-3 font-mono text-gray-500 dark:text-gray-400">+{c.countryCode}</td>
                                  <td className="py-2.5 px-3">
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                                      Not Enabled
                                    </span>
                                  </td>
                                  <td className="py-2.5 px-3 text-center">
                                    <div className="flex items-center justify-center gap-1.5">
                                      <button
                                        type="button"
                                        title="Edit Country"
                                        onClick={() => handleEditCountry(c)}
                                        className="p-1 text-blue-600 hover:text-blue-800 dark:text-blue-400"
                                      >
                                        <PencilIcon className="w-4 h-4" />
                                      </button>
                                      {c.isCustom && (
                                        <button
                                          type="button"
                                          title="Delete Country"
                                          onClick={() => handleDeleteCountry(c)}
                                          className="p-1 text-red-600 hover:text-red-800 dark:text-red-400"
                                        >
                                          <TrashIcon className="w-4 h-4" />
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setCountryModalUser(null)}
                  className="px-4 py-2 text-xs font-semibold text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveCountryModal}
                  className="px-4 py-2 text-xs font-semibold text-white bg-green-600 hover:bg-green-700 rounded-lg shadow-sm"
                >
                  Save Country Rates
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
